import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Box,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useLazyQuery } from "@apollo/client";
import {
  GET_CENTRES,
  GET_EASY_COURSES,
  GET_EASY_TERMS,
  GET_NSDC_GRADES,
} from "../graphql/queries";

const EditRegistrationDialog = ({
  openEdit,
  setOpenEdit,
  regionData,
  schema,
  chosenRegistration,
  entityId,
  entityType,
  onUpdateRegistration,
}) => {
  const [regionId, setRegionId] = useState("");
  const [easyCourses, setEasyCourses] = useState([]);
  const [easyTerms, setEasyTerms] = useState([]);
  const [nsdcGrades, setNsdcGrades] = useState([]);
  const [centres, setCentres] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [getEasyCourses, { loading: loadingEasyCourses }] = useLazyQuery(
    GET_EASY_COURSES,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ getEasyCourses }) => {
        setEasyCourses(getEasyCourses);
      },
    }
  );

  const [getEasyTerms, { loading: loadingEasyTerms }] = useLazyQuery(
    GET_EASY_TERMS,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ getEasyTerms }) => {
        setEasyTerms(getEasyTerms);
      },
    }
  );

  const [getNsdcGrades, { loading: loadingNsdcGrades }] = useLazyQuery(
    GET_NSDC_GRADES,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ getNsdcGrades }) => {
        setNsdcGrades(getNsdcGrades);
      },
    }
  );

  const [getCentres, { loading: loadingCentres }] = useLazyQuery(GET_CENTRES, {
    fetchPolicy: "network-only",
    onCompleted: ({ getCentres }) => {
      setCentres(getCentres);
    },
  });

  const isAnyLoading =
    isLoading ||
    loadingEasyCourses ||
    loadingEasyTerms ||
    loadingNsdcGrades ||
    loadingCentres;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      enrolFrom: null,
      enrolTo: null,
      availableFrom: null,
      availableTo: null,
      easyCourse: null,
      grade: null,
      term: null,
      regionId: "",
      centre: null,
    },
  });

  useEffect(() => {
    if (openEdit && chosenRegistration?.regionId) {
      setRegionId(chosenRegistration.regionId);
    }
  }, [chosenRegistration?.regionId, openEdit]);

  useEffect(() => {
    if (chosenRegistration && regionId) {
      const centre =
        centres?.length > 0
          ? centres.find((c) => c.id === chosenRegistration?.centreId) || null
          : null;
      const easyCourse =
        easyCourses?.length > 0
          ? easyCourses.find(
              (c) => c.name === chosenRegistration?.easyCourseName
            ) || null
          : null;
      const grade =
        nsdcGrades?.length > 0
          ? nsdcGrades.find(
              (g) => g.FullName === chosenRegistration?.gradeName
            ) || null
          : null;
      const term =
        easyTerms?.length > 0
          ? easyTerms.find((t) => t.name === chosenRegistration?.termName) ||
            null
          : null;

      reset({
        enrolFrom: chosenRegistration?.enrolFrom,
        enrolTo: chosenRegistration?.enrolTo,
        availableFrom: chosenRegistration?.availableFrom,
        availableTo: chosenRegistration?.availableTo,
        easyCourse: easyCourse,
        grade: grade,
        term: term,
        regionId: regionId,
        centre: centre,
      });
    }
  }, [
    chosenRegistration,
    reset,
    centres,
    easyCourses,
    nsdcGrades,
    easyTerms,
    regionId,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (regionId) {
        setIsLoading(true);
        try {
          await getEasyCourses({
            variables: {
              regionId,
            },
          });
          await getEasyTerms({
            variables: {
              regionId,
            },
          });
          await getNsdcGrades({
            variables: {
              regionId,
            },
          });
          await getCentres({
            variables: {
              regionId,
            },
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [
    regionId,
    openEdit,
    chosenRegistration?.regionId,
    getEasyCourses,
    getEasyTerms,
    getNsdcGrades,
    getCentres,
  ]);

  const onSubmit = async ({
    enrolFrom,
    enrolTo,
    availableFrom,
    availableTo,
    easyCourse,
    grade,
    term,
    regionId,
    centre,
  }) => {
    try {
      await onUpdateRegistration({
        registrationId: chosenRegistration.id,
        [entityType === "program" ? "programId" : "courseId"]: entityId,
        regionId,
        easyCourseId: easyCourse?.id ? Number(easyCourse.id) : null,
        easyCourseName: easyCourse?.name ?? null,
        enrolFrom: enrolFrom || chosenRegistration?.enrolFrom,
        enrolTo: enrolTo || chosenRegistration?.enrolTo,
        availableFrom: availableFrom || chosenRegistration?.availableFrom,
        availableTo: availableTo || chosenRegistration?.availableTo,
        gradeId: grade?.Region_yearID ?? null,
        gradeName: grade?.FullName ?? null,
        termId: term?.id ? Number(term.id) : null,
        termName: term?.name ?? null,
        centreId: centre?.id ?? null,
      });

      enqueueSnackbar(
        `${
          entityType === "program" ? "Program" : "Course"
        } Registration updated successfully`,
        {
          variant: "success",
        }
      );
      handleClose();
    } catch (error) {
      enqueueSnackbar(
        `Error updating ${
          entityType === "program" ? "Program" : "Course"
        } Registration or Nothing Changed`,
        {
          variant: "error",
        }
      );
    }
  };

  const handleClose = () => {
    setOpenEdit(false);
    setRegionId("");
    setEasyCourses([]);
    setEasyTerms([]);
    setNsdcGrades([]);
    setCentres([]);
    setIsLoading(false);
    reset();
  };

  return (
    <Dialog onClose={handleClose} open={openEdit} maxWidth="xl" fullWidth>
      <DialogTitle>
        Edit {entityType === "program" ? "Program" : "Course"} Registration
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ pt: 1, pb: 1 }}
        >
          <Grid container spacing={0.1} columnSpacing={1}>
            <Grid item xs={3} />
            <Grid item xs={6}>
              <Controller
                name="regionId"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    error={errors.regionId ? true : false}
                  >
                    <InputLabel>Region</InputLabel>
                    <Select
                      label="Region"
                      {...field}
                      value={field.value || ""}
                      disabled={isAnyLoading}
                      onChange={(event) => {
                        field.onChange(event);
                        setRegionId(event.target.value);
                      }}
                    >
                      {regionData.map(({ id, name }) => (
                        <MenuItem value={id} key={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.regionId?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={3} />
            <Grid item xs={6}>
              <Controller
                name="centre"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    error={errors.centre ? true : false}
                  >
                    <Autocomplete
                      options={centres}
                      getOptionLabel={(option) => option.name}
                      value={field.value || null}
                      renderInput={(params) => (
                        <TextField {...params} label="Centre" margin="normal" />
                      )}
                      onChange={(event, newValue) => field.onChange(newValue)}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.centre?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="easyCourse"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    error={errors.easyCourse ? true : false}
                  >
                    <Autocomplete
                      options={easyCourses}
                      getOptionLabel={(option) => option.name}
                      value={field.value || null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="EASy Course"
                          margin="normal"
                        />
                      )}
                      onChange={(event, newValue) => field.onChange(newValue)}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.easyCourse?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="term"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    error={errors.term ? true : false}
                  >
                    <Autocomplete
                      options={easyTerms}
                      getOptionLabel={(option) => option.name}
                      value={field.value || null}
                      renderInput={(params) => (
                        <TextField {...params} label="Term" margin="normal" />
                      )}
                      onChange={(event, newValue) => field.onChange(newValue)}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.term?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="grade"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    error={errors.grade ? true : false}
                  >
                    <Autocomplete
                      options={nsdcGrades}
                      getOptionLabel={(option) => option.FullName}
                      value={field.value || null}
                      renderInput={(params) => (
                        <TextField {...params} label="Grade" margin="normal" />
                      )}
                      onChange={(event, newValue) => field.onChange(newValue)}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.grade?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="enrolFrom"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    required
                    error={errors.enrolFrom ? true : false}
                    sx={{ width: "100%", pb: 3 }}
                  >
                    <DateTimePicker
                      renderInput={(params) => (
                        <TextField {...params} disabled={isAnyLoading} />
                      )}
                      label="Enrolled From"
                      inputFormat="dd/MM/yyyy hh:mm"
                      maxDateTime={watch("enrolTo") || moment().add(1, "year")}
                      minDateTime={moment().subtract(1, "year")}
                      {...field}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.enrolFrom?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="enrolTo"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    required
                    error={errors.enrolTo ? true : false}
                    sx={{ width: "100%", pb: 3 }}
                  >
                    <DateTimePicker
                      renderInput={(params) => (
                        <TextField {...params} disabled={isAnyLoading} />
                      )}
                      label="Enrolled To"
                      inputFormat="dd/MM/yyyy hh:mm"
                      minDateTime={
                        watch("enrolFrom") || moment().subtract(1, "year")
                      }
                      maxDateTime={moment().add(1, "year")}
                      {...field}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.enrolTo?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="availableFrom"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    required
                    error={errors.availableFrom ? true : false}
                    sx={{ width: "100%" }}
                  >
                    <DateTimePicker
                      renderInput={(params) => (
                        <TextField {...params} disabled={isAnyLoading} />
                      )}
                      label="Available From"
                      inputFormat="dd/MM/yyyy hh:mm"
                      maxDateTime={
                        watch("availableTo") || moment().add(1, "year")
                      }
                      minDateTime={moment().subtract(1, "year")}
                      {...field}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.availableFrom?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="availableTo"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    required
                    error={errors.availableTo ? true : false}
                    sx={{ width: "100%" }}
                  >
                    <DateTimePicker
                      renderInput={(params) => (
                        <TextField {...params} disabled={isAnyLoading} />
                      )}
                      label="Available To"
                      inputFormat="dd/MM/yyyy hh:mm"
                      minDateTime={
                        watch("availableFrom") || moment().subtract(1, "year")
                      }
                      maxDateTime={moment().add(1, "year")}
                      {...field}
                      disabled={isAnyLoading}
                    />
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.availableTo?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={10} />
            <Grid item xs={2}>
              <Box>
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isAnyLoading}
                  sx={{ mr: 2 }}
                >
                  Submit
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isAnyLoading}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditRegistrationDialog;
